import React from 'react';
import { Link } from 'react-router-dom';
import { ClompApi } from '../api/ClompApi';
import { timeAgo } from '../api/presence';
import './NotificationBell.css';

const api = new ClompApi();

// Sent on window when a friend request is answered from the bell, so a profile showing friends can show the change
export const FRIENDS_CHANGED_EVENT = 'clomp-friends-changed';

// How often the bell asks for new notifications while the page is on screen
const CHECK_MS = 60 * 1000;

// The bell in the navbar, for logged-in players: a count of unread notifications on it, and a list of the newest
// when it is opened. Opening it marks them read. A friend request can be answered from the list.
export class NotificationBell extends React.Component {

    state = {
        open: false,
        loaded: false,
        unread: 0,
        notifications: [],
        // the id of the notification whose request is being answered
        answering: null
    };

    componentDidMount() {
        this.mounted = true;
        this.load();
        this.timer = setInterval(() => !document.hidden && this.load(), CHECK_MS);
        document.addEventListener('visibilitychange', this.onVisible);
        document.addEventListener('mousedown', this.onOutside);
        document.addEventListener('keydown', this.onKeyDown);
    }

    componentWillUnmount() {
        this.mounted = false;
        clearInterval(this.timer);
        document.removeEventListener('visibilitychange', this.onVisible);
        document.removeEventListener('mousedown', this.onOutside);
        document.removeEventListener('keydown', this.onKeyDown);
    }

    box = React.createRef();

    onVisible = () => {
        if (!document.hidden) {
            this.load();
        }
    }

    onOutside = (e) => {
        if (this.state.open && this.box.current && !this.box.current.contains(e.target)) {
            this.setState({ open: false });
        }
    }

    onKeyDown = (e) => {
        if (e.key === 'Escape' && this.state.open) {
            this.setState({ open: false });
        }
    }

    load() {
        if (localStorage.getItem('token') === null) {
            return Promise.resolve();
        }
        return api.friends.getNotifications().then((data) => {
            if (this.mounted) {
                this.setState({ loaded: true, unread: data.unread, notifications: data.notifications });
            }
        }).catch(() => { /* tried again next time */ });
    }

    toggle = () => {
        let open = !this.state.open;
        this.setState({ open });
        if (open && this.state.unread > 0) {
            // shown as unread this once, then read from now on
            api.friends.markRead().then(() => this.mounted && this.setState({ unread: 0 })).catch(() => {});
        }
    }

    answer(notification, accept) {
        this.setState({ answering: notification.id });
        let request = accept ? api.friends.add(notification.from.id) : api.friends.remove(notification.from.id);
        request.then(() => {
            window.dispatchEvent(new Event(FRIENDS_CHANGED_EVENT));
            return this.load();
        }).catch(() => {}).then(() => this.mounted && this.setState({ answering: null }));
    }

    renderItem(notification) {
        let from = notification.from;
        let who = from ? <Link to={`/profile/${encodeURIComponent(from.username)}`} onClick={() => this.setState({ open: false })}>{from.username}</Link> : 'Someone';
        let text = notification.kind === 'friend_request'
            // a request that was declined or withdrawn is taken back, so one that isn't waiting was accepted
            ? notification.pending ? <>{who} wants to be your friend.</> : <>You're now friends with {who}.</>
            : notification.kind === 'friend_accepted' ? <>{who} accepted your friend request.</>
            : <>Something happened.</>;
        let busy = this.state.answering === notification.id;
        return (
            <li key={notification.id} className={notification.read ? '' : 'is-unread'}>
                <p className="nb-text">{text}</p>
                {notification.kind === 'friend_request' && notification.pending && from ?
                    <div className="nb-answer">
                        <button type="button" className="nb-btn nb-btn-primary" disabled={busy} onClick={() => this.answer(notification, true)}>Accept</button>
                        <button type="button" className="nb-btn" disabled={busy} onClick={() => this.answer(notification, false)}>Decline</button>
                    </div>
                    : null}
                <p className="nb-time">{timeAgo(notification.createdAt)}</p>
            </li>
        );
    }

    render() {
        let { open, unread, notifications, loaded } = this.state;
        let label = unread > 0 ? `Notifications (${unread} unread)` : 'Notifications';
        return (
            <div className="nb" ref={this.box}>
                <button type="button" className="nb-bell" aria-label={label} title={label} aria-expanded={open} aria-haspopup="true" onClick={this.toggle}>
                    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                        <path fill="currentColor" d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22zm7-6V11a7 7 0 0 0-5.5-6.84V3.5a1.5 1.5 0 0 0-3 0v.66A7 7 0 0 0 5 11v5l-2 2v1h18v-1l-2-2z" />
                    </svg>
                    {unread > 0 ? <span className="nb-count" aria-hidden="true">{unread > 9 ? '9+' : unread}</span> : null}
                </button>
                {open ?
                    <div className="nb-panel" role="region" aria-label="Notifications">
                        <h2>Notifications</h2>
                        {!loaded ? <p className="nb-empty">Loading...</p>
                            : notifications.length === 0 ? <p className="nb-empty">Nothing yet. When someone adds you as a friend, you'll see it here.</p>
                            : <ul>{notifications.map((notification) => this.renderItem(notification))}</ul>}
                    </div>
                    : null}
            </div>
        );
    }

}
